import { IClient, IPluginBinding, IPluginCard, IPluginNotificationSendDto } from "@lib";
import { Modal } from "@core/components/alt-ui/modal";
import {
    Alert,
    Button,
    Control,
    Html,
    Label,
    Panel,
    PhoneBox,
    Select,
    TextArea,
} from "@core/components/alt-ui/controls";
import { AltMessage } from "@/utils/plugins/alt-message";

export interface IOrderViewNotificationModalContext {
    plugins: IPluginBinding[];
    client?: IClient;
    message: string;
    messageBox: AltMessage;
}

export class OrderViewNotificationModal extends Modal<IOrderViewNotificationModalContext> {
    private tbPhone!: PhoneBox;
    private taMessage!: TextArea;
    private lbLength!: Label;
    private cbPlugin!: Select<IPluginBinding>;
    private alPlugin!: Html;

    private pnlFooter!: Panel;
    private btnCancel!: Button;
    private btnSave!: Button;

    private context: IOrderViewNotificationModalContext | null = null;
    public onSend: ((bindingId: string, dto: IPluginNotificationSendDto) => Promise<boolean>) | null = null;

    public constructor() {
        super("order-view-notification-modal", "Отправка уведомлений");
        this.initializeControls();
    }

    public show(context: IOrderViewNotificationModalContext): Promise<void> {
        this.context = context;
        this.initializeControls();
        this.populateControls(context);
        return super.show();
    }

    protected initializeControls(): void {
        this.tbPhone = new PhoneBox();
        this.tbPhone.id = "order-view-notification.phone";
        this.tbPhone.label = "Телефон";
        this.tbPhone.validation = "required";

        this.lbLength = new Label();
        this.lbLength.text = "";
        this.lbLength.class = "text-sm text-right -mt-0.5";

        this.taMessage = new TextArea();
        this.taMessage.id = "order-view-notification.message";
        this.taMessage.label = "Сообщение";
        this.taMessage.validation = "required";
        this.taMessage.addTextChangedHandler(() => this.changeMessage());

        this.cbPlugin = new Select<IPluginBinding>();
        this.cbPlugin.id = "order-view-notification.plugin";
        this.cbPlugin.label = "Сервис уведомлений";
        this.cbPlugin.help = 'Выбрать и настроить можно в меню "Настройки компании" - Интеграции.';
        this.cbPlugin.items = this.context?.plugins ?? [];
        this.cbPlugin.textField = p => p.pluginRef?.name ?? "";
        this.cbPlugin.descriptionField = p => p.pluginRef?.description ?? "";
        this.cbPlugin.selectedIndex = this.cbPlugin.items.length > 0 ? 0 : -1;
        this.cbPlugin.visible = this.cbPlugin.items.length > 1;
        this.cbPlugin.addChangedHandler((s, e) => this.changePlugin(e.item));

        this.alPlugin = new Html();
        // <a href="https://smsaero.ru/price/sms/" target="_blank"></a>
        this.alPlugin.html = `<div class="p-1" style="background-color: rgba(255, 159, 67, 0.12); color: rgb(255, 159, 67)">
            Сервис СМС рассылок «SMS Aero» изменил политику ценообразования, и цена за СМС сильно выросла (вплоть до 46 р/смс).
            <br/><br/>
            Поэтому мы временно приостановили отправку сообщений.
            <br/><br/>
            Но всё ещё остаётся возможность подключить собственный аккаунт «SMS Aero» или «SMS.ru» в Настройках компании - Интеграции.
            </div>`;
        this.alPlugin.visible = false;

        //

        this.btnCancel = new Button();
        this.btnCancel.id = "order-view-notification.cancel";
        this.btnCancel.text = "Отменить";
        this.btnCancel.class = "mr-0.75";
        this.btnCancel.variant = "outline-danger";
        this.btnCancel.addClickHandler(() => this.clickCancel());

        this.btnSave = new Button();
        this.btnSave.id = "order-view-notification.send";
        this.btnSave.text = "Отправить";
        this.btnSave.addClickHandler(() => this.clickSend());

        this.pnlFooter = new Panel();
        this.pnlFooter.class = "flex justify-end mt-2";
        this.pnlFooter.addControls([this.btnCancel, this.btnSave]);
    }

    private populateControls(context: IOrderViewNotificationModalContext): void {
        this.tbPhone.text = context.client?.info?.contacts?.phone ?? "";
        this.taMessage.text = context.message;
        this.changePlugin(this.cbPlugin.selectedItem);
    }

    public get controls(): Control[] {
        return [this.tbPhone, this.taMessage, this.lbLength, this.cbPlugin, this.alPlugin, this.pnlFooter];
    }

    private changeMessage(): void {
        this.lbLength.text = `Символов: ${this.taMessage.text.length}`;
    }

    private changePlugin(plugin: IPluginBinding | null): void {
        if (plugin?.code === "2fc34ea4-fb21-453e-bbee-3d8eceaf0ef4") {
            this.alPlugin.visible = true;
            this.btnSave.text = "Всё равно отправить";
            this.btnSave.variant = "warning";
        } else {
            this.alPlugin.visible = false;
            this.btnSave.text = "Отправить";
            this.btnSave.variant = "primary";
        }
    }

    private async clickCancel(): Promise<void> {
        this.hide();
    }

    private async clickSend(): Promise<void> {
        const valid = await this.validate();

        if (!valid || !this.context || !this.cbPlugin.selectedItem || !this.onSend) {
            return;
        }

        if (this.cbPlugin.selectedItem.code === "2fc34ea4-fb21-453e-bbee-3d8eceaf0ef4") {
            const confirm = await this.context.messageBox.confirm(
                "Цена смс зависит от оператора связи и длины и может достигать 46 р. Всё равно отправить?",
                "Отправка смс",
                { okText: "Отправить" },
            );

            if (!confirm) {
                return;
            }
        }

        const bindingId = this.cbPlugin.selectedItem.id;
        const phone = this.tbPhone.text.trim().replace(" ", "");

        const dto: IPluginNotificationSendDto = {
            to: phone,
            message: this.taMessage.text,
        };

        const result = await this.onSend(bindingId, dto);

        if (result) {
            this.hide(result);
        }
    }
}
